import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { generateCoverLetter, extractKeywords } from '../services/openaiService';
import { Document, Packer, Paragraph, TextRun, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';
import * as mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';
import Header from './Header';
import Footer from './Footer';
import CreditsBadge from './CreditsBadge';
import './CoverLetterPage.css';
import ProductSidebar from './ProductSidebar';

// Set PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const CoverLetterPage = ({ embedded = false } = {}) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { user, credits, setCredits, fetchCredits } = useUser();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

  const [jobDescription, setJobDescription] = useState('');
  const [resumeText, setResumeText] = useState('');
  const [resumeSource, setResumeSource] = useState(null); // 'profile' | 'upload' | null
  const [profileData, setProfileData] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [hiringManagerName, setHiringManagerName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [coverLetter, setCoverLetter] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isReadingFile, setIsReadingFile] = useState(false);
  const [error, setError] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);

  const [personalInfo, setPersonalInfo] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    linkedIn: ''
  });

  // ── Profile fetch + helper to build a resume from profile fields ───
  // The user picks between their profile resume and a fresh upload via the
  // two-card chooser below. We only store the profile here — resumeText is
  // populated when the user actually clicks a card.
  const buildResumeTextFromProfile = (profile) => {
    let text = '';
    if (profile.fullName) text += `${profile.fullName}\n`;
    if (profile.jobTitle) text += `${profile.jobTitle}\n`;
    if (profile.email) text += `${profile.email}\n`;
    if (profile.phone) text += `Phone: ${profile.phone}\n`;
    if (profile.location) text += `Location: ${profile.location}\n`;
    if (profile.linkedIn) text += `LinkedIn: ${profile.linkedIn}\n`;
    text += '\n';
    if (profile.professionalSummary) text += `PROFESSIONAL SUMMARY\n${profile.professionalSummary}\n\n`;
    if (profile.skills) text += `SKILLS\n${profile.skills}\n\n`;
    if (Array.isArray(profile.workExperience) && profile.workExperience.some(e => e.company || e.position)) {
      text += 'WORK EXPERIENCE\n';
      profile.workExperience.forEach(exp => {
        if (exp.position || exp.company) {
          text += `${exp.position || ''}${exp.company ? ` at ${exp.company}` : ''}${exp.duration ? ` (${exp.duration})` : ''}\n`;
          if (exp.description) text += `${exp.description}\n`;
          text += '\n';
        }
      });
    }
    if (Array.isArray(profile.education) && profile.education.some(e => e.degree || e.institution)) {
      text += 'EDUCATION\n';
      profile.education.forEach(edu => {
        if (edu.degree || edu.institution) {
          text += `${edu.degree || ''}${edu.field ? ` in ${edu.field}` : ''}${edu.institution ? ` - ${edu.institution}` : ''}${edu.year ? ` (${edu.year})` : ''}\n`;
        }
      });
    }
    return text.trim();
  };

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/profile`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled || !data?.profile) return;
        setProfileData(data.profile);
        // Pre-fill personalInfo for the cover letter signature, regardless
        // of which resume the user picks.
        setPersonalInfo(prev => ({
          fullName: prev.fullName || data.profile.fullName || '',
          email: prev.email || data.profile.email || '',
          phone: prev.phone || data.profile.phone || '',
          location: prev.location || data.profile.location || '',
          linkedIn: prev.linkedIn || data.profile.linkedIn || '',
        }));
      })
      .catch(() => { /* fail silently — manual upload still works */ });
    return () => { cancelled = true; };
  }, [user, API_BASE_URL]);

  // True only if the profile actually has resume content worth using.
  const hasProfileResume = !!(
    profileData && (
      profileData.professionalSummary ||
      profileData.skills ||
      (Array.isArray(profileData.workExperience) &&
        profileData.workExperience.some(e => e.company || e.position))
    )
  );

  // Extract raw text from PDF using pdf.js
  const extractTextFromPDF = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let fullText = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      const pageText = content.items.map(item => item.str).join(' ');
      fullText += pageText + '\n';
    }
    return fullText.trim();
  };

  // Extract raw text from DOCX using mammoth
  const extractTextFromDOCX = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value.trim();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword'
    ];

    if (!allowedTypes.includes(file.type)) {
      setUploadError('Please upload a PDF or DOCX file only.');
      return;
    }

    setIsReadingFile(true);
    setUploadError('');
    setUploadedFileName(file.name);
    setResumeText('');

    try {
      let text = '';

      if (file.type === 'application/pdf') {
        text = await extractTextFromPDF(file);
      } else {
        text = await extractTextFromDOCX(file);
      }

      if (!text) {
        throw new Error('No text could be extracted from the file');
      }

      setResumeText(text);
      // Uploaded file takes precedence over profile resume.
      setResumeSource('upload');
    } catch (err) {
      console.error('Error reading file:', err);
      setUploadError(`Failed to read file: ${err.message}`);
      setUploadedFileName('');
    } finally {
      setIsReadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Deduct credit function
  const deductCredit = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cover-letter/deduct-credit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 403) {
          setShowInsufficientCreditsModal(true);
          return false;
        }
        throw new Error(data.error || 'Failed to deduct credit');
      }

      if (setCredits) {
        setCredits(data.remainingCredits);
      }

      return true;
    } catch (error) {
      console.error('Error deducting credit:', error);
      setError('Failed to process credit transaction');
      return false;
    }
  };

  // Save cover letter to database
  const saveCoverLetterToDatabase = async (coverLetterContent) => {
    try {
      const fileName = `Cover_Letter_${jobTitle.replace(/\s+/g, '_')}_${companyName.replace(/\s+/g, '_') || 'Company'}.docx`;

      const response = await fetch(`${API_BASE_URL}/api/cover-letter/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          coverLetterContent,
          jobTitle,
          companyName,
          jobDescription,
          keywords,
          personalInfo,
          fileName
        })
      });

      if (response.ok) {
        console.log('Cover letter saved successfully');
        return true;
      }
      return false;
    } catch (saveError) {
      console.error('Error saving cover letter:', saveError);
      return false;
    }
  };

  const handleGenerateCoverLetter = async () => {
    if (!user) {
      setShowAuthModal(true);
      setError('Please login to generate cover letters');
      return;
    }

    if (!resumeText.trim()) {
      setError('Please upload your resume or paste your resume text');
      return;
    }

    if (!jobDescription.trim()) {
      setError('Please enter the job description');
      return;
    }

    if (!jobTitle.trim()) {
      setError('Please enter the job title');
      return;
    }

    if (credits < 1) {
      setShowInsufficientCreditsModal(true);
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      const creditDeducted = await deductCredit();

      if (!creditDeducted) {
        setIsGenerating(false);
        return;
      }

      const extractedKeywords = await extractKeywords(jobDescription);
      setKeywords(extractedKeywords);

      const generated = await generateCoverLetter(
        resumeText,
        jobDescription,
        extractedKeywords,
        companyName,
        hiringManagerName,
        jobTitle,
        personalInfo
      );

      setCoverLetter(generated);
      await saveCoverLetterToDatabase(generated);

      if (fetchCredits) {
        fetchCredits();
      }
    } catch (error) {
      console.error('Error generating cover letter:', error);
      setError('Failed to generate cover letter. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClearAll = () => {
    setJobDescription('');
    setResumeText('');
    setResumeSource(null);
    setCompanyName('');
    setHiringManagerName('');
    setJobTitle('');
    setUploadedFileName('');
    setKeywords([]);
    setCoverLetter('');
    setError('');
    setUploadError('');
    setPersonalInfo({ fullName: '', email: '', phone: '', location: '', linkedIn: '' });
  };

  const handleDownloadWord = async () => {
    if (!coverLetter) {
      setError('Please generate a cover letter first');
      return;
    }

    try {
      const docChildren = [];
      const lines = coverLetter.split('\n');
      let lineIndex = 0;

      while (lineIndex < lines.length && !lines[lineIndex].trim()) lineIndex++;
      if (lineIndex < lines.length) {
        docChildren.push(new Paragraph({
          children: [new TextRun({ text: lines[lineIndex].trim(), font: 'Times New Roman', size: 36, bold: true })],
          spacing: { after: 120, line: 360 },
          alignment: AlignmentType.CENTER
        }));
        lineIndex++;
      }

      while (lineIndex < lines.length && !lines[lineIndex].trim()) lineIndex++;
      if (lineIndex < lines.length) {
        docChildren.push(new Paragraph({
          children: [new TextRun({ text: lines[lineIndex].trim(), font: 'Times New Roman', size: 24 })],
          spacing: { after: 360, line: 360 },
          alignment: AlignmentType.CENTER
        }));
        lineIndex++;
      }

      for (let i = lineIndex; i < lines.length; i++) {
        const trimmedLine = lines[i].trim();
        if (!trimmedLine) continue;

        if (trimmedLine.toLowerCase().startsWith('sincerely')) {
          docChildren.push(new Paragraph({
            children: [new TextRun({ text: trimmedLine, font: 'Times New Roman', size: 24 })],
            spacing: { before: 240, after: 0, line: 360 },
            alignment: AlignmentType.LEFT
          }));
          if (personalInfo.fullName) {
            docChildren.push(new Paragraph({
              children: [new TextRun({ text: personalInfo.fullName, font: 'Times New Roman', size: 24 })],
              spacing: { before: 0, line: 360 },
              alignment: AlignmentType.LEFT
            }));
          }
          break;
        }

        docChildren.push(new Paragraph({
          children: [new TextRun({ text: trimmedLine, font: 'Times New Roman', size: 24 })],
          spacing: { after: 180, line: 360 },
          alignment: AlignmentType.LEFT
        }));
      }

      const doc = new Document({
        sections: [{
          properties: { page: { margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 } } },
          children: docChildren
        }]
      });

      const blob = await Packer.toBlob(doc);
      const fileName = `Cover_Letter_${jobTitle.replace(/\s+/g, '_')}_${companyName.replace(/\s+/g, '_') || 'Company'}.docx`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error('Error generating Word document:', error);
      setError('Failed to generate Word document. Please try again.');
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(coverLetter);
    alert('Cover letter copied to clipboard!');
  };

  const highlightKeywords = (text, keywords) => {
    if (!text || keywords.length === 0) return text;
    let highlightedText = text;
    keywords.forEach(keyword => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
      highlightedText = highlightedText.replace(regex, '<strong>$&</strong>');
    });
    return { __html: highlightedText };
  };

  return (
    <div className={embedded ? 'cl-embedded-root' : 'neural-glass-homepage'}>
      {!embedded && <div className="neural-background"></div>}

      {!embedded && (
        <div className="geometric-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>
      )}

      <div
        className={`cover-letter-page-container ${embedded ? 'cover-letter-embedded' : ''}`}
        style={embedded ? undefined : { '--page-hero-img': "url('/images/page-heroes/cover-letter.png')" }}
      >
        {!embedded && <Header setShowAuthModal={setShowAuthModal} />}
        {!embedded && <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />}

        {showInsufficientCreditsModal && (
          <div className="modal-overlay" onClick={() => setShowInsufficientCreditsModal(false)}>
            <div className="modal-content glass" onClick={(e) => e.stopPropagation()}>
              <h3>Insufficient Credits</h3>
              <p>You need at least 1 credit to generate a cover letter.</p>
              <p>Current credits: <strong>{credits}</strong></p>
              <div className="modal-actions">
                <button className="modal-btn primary" onClick={() => {
                  setShowInsufficientCreditsModal(false);
                  document.dispatchEvent(new CustomEvent('openPricingModal'));
                }}>
                  Get More Credits
                </button>
                <button className="modal-btn" onClick={() => setShowInsufficientCreditsModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <main className="cover-letter-page-main">
          {!embedded && (
            <div className="li-hero">
              <div className="li-hero-badge">🚀 AI-Powered</div>
              <h1 className="li-hero-title">
                Cover <span className="li-gradient-text">Letter Generator</span>
              </h1>
              <p className="li-hero-subtitle">
                Generate compelling AI-powered cover letters that help you stand out in every job application.
              </p>
              <CreditsBadge feature="coverLetter" label="Cover Letter" className="hero-credits-badge" />
            </div>
          )}
          <div className="cover-letter-main-container">
            <div className="cover-letter-content">

              {/* Left Panel */}
              <div className="left-panel">

                {/* Job Details */}
                <div className="job-details-section">
                  <div className="job-details-container glass">
                    <div className="section-header">
                      <h3>Job Details</h3>
                      <button
                        onClick={handleClearAll}
                        className="clear-button"
                        disabled={isGenerating || isReadingFile}
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="form-grid-cl">
                      <div className="form-group-cl">
                        <label>Job Title *</label>
                        <input
                          type="text"
                          value={jobTitle}
                          onChange={(e) => setJobTitle(e.target.value)}
                          placeholder="e.g., Senior Software Engineer"
                          disabled={isGenerating || isReadingFile}
                        />
                      </div>
                      <div className="form-group-cl">
                        <label>Company Name</label>
                        <input
                          type="text"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          placeholder="e.g., Tech Corp"
                          disabled={isGenerating || isReadingFile}
                        />
                      </div>
                      <div className="form-group-cl full-width">
                        <label>Hiring Manager Name (Optional)</label>
                        <input
                          type="text"
                          value={hiringManagerName}
                          onChange={(e) => setHiringManagerName(e.target.value)}
                          placeholder="e.g., Jane Smith"
                          disabled={isGenerating || isReadingFile}
                        />
                      </div>
                      <div className="form-group-cl full-width">
                        <label>Job Description *</label>
                        <textarea
                          value={jobDescription}
                          onChange={(e) => { setJobDescription(e.target.value); setError(''); }}
                          placeholder="Paste the complete job description here..."
                          rows="8"
                          disabled={isGenerating || isReadingFile}
                        ></textarea>
                      </div>
                      
                      {/* Resume Section — two-card chooser when profile resume
                          exists, plain upload area otherwise. The editable
                          textarea is intentionally not rendered: resumeText
                          flows silently into the API call. */}
                      <div className="form-group-cl full-width">
                        <label className="resume-section-label">Your Resume/CV *</label>

                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,.docx,.doc"
                          onChange={handleFileUpload}
                          disabled={isReadingFile || isGenerating}
                          style={{ display: 'none' }}
                        />

                        {hasProfileResume ? (
                          <>
                            <div className="resume-choice-buttons-cl">
                              <button
                                type="button"
                                onClick={() => {
                                  const text = buildResumeTextFromProfile(profileData);
                                  setResumeText(text);
                                  setResumeSource('profile');
                                  setUploadError('');
                                }}
                                className={`resume-choice-btn-cl${resumeSource === 'profile' ? ' resume-choice-btn-cl-active' : ''}`}
                                disabled={isReadingFile || isGenerating}
                              >
                                <div className="resume-choice-label-cl">✅ Use Profile Resume</div>
                                {profileData.fullName && (
                                  <div className="resume-choice-sublabel-cl">{profileData.fullName}</div>
                                )}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setResumeSource('upload');
                                  setResumeText('');
                                  setUploadedFileName('');
                                  setUploadError('');
                                }}
                                className={`resume-choice-btn-cl${resumeSource === 'upload' ? ' resume-choice-btn-cl-active' : ''}`}
                                disabled={isReadingFile || isGenerating}
                              >
                                <div className="resume-choice-label-cl">📄 Upload New Resume</div>
                                <div className="resume-choice-sublabel-cl">Choose a different file</div>
                              </button>
                            </div>

                            {resumeSource === 'profile' && (
                              <div className="profile-resume-confirm-cl">
                                ✓ Profile resume loaded and ready to use.
                              </div>
                            )}

                            {resumeSource === 'upload' && (
                              <div className="upload-area-cl">
                                <button
                                  type="button"
                                  onClick={() => fileInputRef.current?.click()}
                                  className="file-upload-label-cl"
                                  disabled={isReadingFile || isGenerating}
                                >
                                  {isReadingFile ? 'Reading resume…' : 'Upload Resume (PDF/DOCX)'}
                                </button>
                                <p className="upload-hint-cl">
                                  {uploadedFileName
                                    ? `✓ ${uploadedFileName}`
                                    : 'Choose a PDF or DOCX file to upload.'}
                                </p>
                              </div>
                            )}

                            {!resumeSource && (
                              <p className="upload-hint-cl">Select an option above to provide your resume.</p>
                            )}
                          </>
                        ) : (
                          <div className="upload-area-cl">
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              className="file-upload-label-cl"
                              disabled={isReadingFile || isGenerating}
                            >
                              {isReadingFile ? 'Reading resume…' : '📄 Upload Resume (PDF/DOCX)'}
                            </button>
                            <p className="upload-hint-cl">
                              {uploadedFileName
                                ? `✓ ${uploadedFileName}`
                                : 'Upload your resume to generate a personalized cover letter.'}
                            </p>
                          </div>
                        )}

                        {uploadError && (
                          <div className="parse-error">⚠️ {uploadError}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>



                {/* Generate Button */}
                <div className="generate-section">
                  <button
                    onClick={handleGenerateCoverLetter}
                    className="generate-btn-main"
                    disabled={isGenerating || isReadingFile || !resumeText.trim() || !jobDescription.trim() || !jobTitle.trim()}
                  >
                    {isGenerating ? (
                      <span className="loading">
                        <span className="typing-indicator">
                          <span></span>
                          <span></span>
                          <span></span>
                        </span>
                        Generating Cover Letter...
                      </span>
                    ) : (
                      'Generate Cover Letter'
                    )}
                  </button>
                </div>
              </div>

              {/* Right Panel */}
              <div className="right-panel">
                <div className="cover-letter-output-section">
                  <div className="cover-letter-container glass">
                    <div className="section-header">
                      <h3>Generated Cover Letter</h3>
                      <div className="action-buttons">
                        <button
                          onClick={handleCopyToClipboard}
                          className="copy-button"
                          disabled={isGenerating || !coverLetter}
                        >
                          Copy
                        </button>
                        <button
                          onClick={handleDownloadWord}
                          className="download-button"
                          disabled={isGenerating || !coverLetter}
                        >
                          Download Word
                        </button>
                      </div>
                    </div>
                    <div className="cover-letter-text">
                      {isGenerating ? (
                        <div className="loading-placeholder">
                          <span className="typing-indicator">
                            <span></span>
                            <span></span>
                            <span></span>
                          </span>
                          Generating your personalized cover letter...
                        </div>
                      ) : coverLetter ? (
                        <div dangerouslySetInnerHTML={highlightKeywords(coverLetter, keywords)} />
                      ) : (
                        <div className="placeholder">
                          Your tailored cover letter will appear here after generation...
                          <br /><br />
                          <small>Quick steps:</small>
                          <ul>
                            <li>Fill in job details and description</li>
                            <li>Upload resume or paste text directly</li>
                            <li>Click "Generate Cover Letter"</li>
                          </ul>
                        </div>
                      )}
                    </div>
                    {coverLetter && (
                      <div className="character-count">
                        {coverLetter.length} characters
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className="error-message glass">{error}</div>
            )}
          </div>

          {!embedded && (
          <>
          <section className="info-section glass">
            <h2 className="section-title">Why Use Our Cover Letter Generator?</h2>
            <div className="info-grid">
              <div className="info-card glass">
                <div className="info-icon">✍️</div>
                <h3>Personalized Content</h3>
                <p>Our AI analyzes your resume and the job description to create a cover letter that highlights your most relevant skills and experiences for the specific position.</p>
              </div>
              <div className="info-card glass">
                <div className="info-icon">⚡</div>
                <h3>Save Time</h3>
                <p>Generate a professional cover letter in minutes instead of hours. Our tool does the heavy lifting while you focus on applying to more jobs.</p>
              </div>
              <div className="info-card glass">
                <div className="info-icon">🎯</div>
                <h3>Keyword Optimization</h3>
                <p>Automatically extracts key terms from the job description and naturally incorporates them into your cover letter to catch recruiters' attention.</p>
              </div>
            </div>
          </section>

          <section className="how-it-works glass">
            <h2 className="section-title">How It Works</h2>
            <div className="steps-grid">
              <div className="step-card glass">
                <div className="step-number">1</div>
                <h4>Enter Job Details</h4>
                <p>Fill in the job title, company name, and paste the complete job description to help our AI understand what the employer is looking for.</p>
              </div>
              <div className="step-card glass">
                <div className="step-number">2</div>
                <h4>Upload Your Resume</h4>
                <p>Upload your resume in PDF or DOCX format. Text is extracted instantly into an editable textarea — no AI parsing, no credits used at this step.</p>
              </div>
              <div className="step-card glass">
                <div className="step-number">3</div>
                <h4>Generate Cover Letter</h4>
                <p>Click the generate button and our AI will craft a tailored cover letter that matches your background with the job requirements.</p>
              </div>
              <div className="step-card glass">
                <div className="step-number">4</div>
                <h4>Review & Download</h4>
                <p>Review your personalized cover letter, make any desired edits, and download it as a Word document ready for submission.</p>
              </div>
            </div>
          </section>

          <section className="tips-section glass">
            <h2 className="section-title">Cover Letter Best Practices</h2>
            <div className="tips-grid">
              <div className="tip-card glass">
                <div className="tip-header"><span className="tip-icon">✓</span><h4>Customize for Each Job</h4></div>
                <p>Always tailor your cover letter to the specific job and company. Generic cover letters are easily spotted and often ignored by recruiters.</p>
              </div>
              <div className="tip-card glass">
                <div className="tip-header"><span className="tip-icon">✓</span><h4>Show Enthusiasm</h4></div>
                <p>Express genuine interest in the role and company. Explain why you're excited about this opportunity and how it aligns with your career goals.</p>
              </div>
              <div className="tip-card glass">
                <div className="tip-header"><span className="tip-icon">✓</span><h4>Highlight Achievements</h4></div>
                <p>Focus on specific accomplishments and results rather than just listing responsibilities. Use numbers and metrics when possible.</p>
              </div>
              <div className="tip-card glass">
                <div className="tip-header"><span className="tip-icon">✓</span><h4>Keep It Concise</h4></div>
                <p>Aim for 3-4 paragraphs and stay under one page. Recruiters appreciate brevity and clarity over lengthy explanations.</p>
              </div>
              <div className="tip-card glass">
                <div className="tip-header"><span className="tip-icon">✓</span><h4>Address the Hiring Manager</h4></div>
                <p>Whenever possible, address your cover letter to a specific person. It shows you've done your research and adds a personal touch.</p>
              </div>
              <div className="tip-card glass">
                <div className="tip-header"><span className="tip-icon">✓</span><h4>Proofread Carefully</h4></div>
                <p>Always review your cover letter for typos, grammar errors, and formatting issues. A polished letter shows attention to detail and professionalism.</p>
              </div>
            </div>
          </section>
          </>
          )}
        </main>

        {!embedded && <Footer />}
      </div>
    </div>
  );
};

export default CoverLetterPage;